// @ts-check

import { infiniteQueryOptions, mutationOptions, queryOptions } from "@tanstack/react-query"
import { createChatSession, deleteChatSession, getChatSession, getChatSessions, updateChatSession } from "../api"
import { ChatImagesQueryKeys } from "../images"
import { ChatMessageQueryKeys } from "../messages/query"

const ChatSessionQueryKeys = {
  /** @param {string} projectId */
  CHAT_SESSION_LIST: projectId => ["session-lists", projectId ?? null],
  /** @param {string} sessionId */
  CHAT_SESSION: sessionId => ["sessions", sessionId],
}

/** @typedef {import("@tanstack/react-query").InfiniteData<{ sessions: ChatSession[], nextCursor: string }, string>} ChatSessionsQueryData */
/** @typedef {ChatSession} ChatSessionQueryData */

const ChatSessionQueryActions = {
  /**
   * @param {string} sessionId
   * @param {ChatSession} sessionInfo
   * @param {Object} [options]
   * @param {boolean} [options.replace]
   */
  UPDATE_SESSION_LIST_ENTRY(sessionId, sessionInfo, { replace = false } = {}) {
    /** @param {ChatSessionsQueryData} sessionsData */
    return sessionsData => {
      if(sessionsData == null) return
      
      return {
        ...sessionsData,
        pages: sessionsData?.pages.map(page => ({
          ...page,
          sessions: page.sessions.map(session => {
            if (session._id !== sessionId) return session

            if (replace) return sessionInfo
            return { ...session, ...sessionInfo }
          }),
        })),
      }
    }
  },

  /** @param {ChatSession} sessionInfo */
  ADD_SESSION_LIST_ENTRY(sessionInfo) {
    /** @param {ChatSessionsQueryData} sessionsData */
    return sessionsData => ({
      ...sessionsData,
      pages: sessionsData?.pages.map((page, pageIndex) => {
        if(pageIndex !== 0) return page
        return {
          ...page,
          sessions: [sessionInfo, ...(page.sessions ?? [])],
        }
      }),
    })
  },

  /** @param {string} sessionId */
  REMOVE_SESSION_LIST_ENTRY(sessionId) {
    /** @param {ChatSessionsQueryData} sessionsData */
    return sessionsData => ({
      ...sessionsData,
      pages: sessionsData?.pages.map(page => ({
        ...page,
        sessions: page.sessions.filter(session => session._id !== sessionId),
      })),
    })
  },
}

const FIVE_MINUTES_MS = 5 * 60 * 1000
const MAX_PAGE_SIZE = 15

const ChatSessionQueryOptions = {
  /**
   * @param {Object} params
   * @param {string} params.userToken
   * @param {string} [params.projectId]
   */
  CHAT_SESSION_LIST: ({ userToken, projectId }) => infiniteQueryOptions({
    queryKey: ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
    queryFn: ({ pageParam, signal, client }) => (
      getChatSessions(userToken, {
        projectId,
        pageSize: MAX_PAGE_SIZE,
        cursor: pageParam,
        signal,
      }).then(async (data) => {
        await Promise.allSettled(
          data.thumbnails.map(thumbnail => (
            client.cancelQueries({
              queryKey: ChatImagesQueryKeys.CHAT_SESSION_THUMBNAIL_IMAGE(thumbnail.sessionId, projectId),
            })
          ))
        )

        for (const thumbnail of data.thumbnails) {
          client.setQueryData(
            ChatImagesQueryKeys.CHAT_SESSION_THUMBNAIL_IMAGE(thumbnail.sessionId, projectId),
            thumbnail.image,
          )
        }

        return {
          sessions: data.sessions,
          nextCursor: data.nextCursor,
        }
      })
    ),
    select: data => {
      const allSessions = data.pages.flatMap(page => page.sessions)
      const uniqueSessionsMap = new Map()
      
      for (const session of allSessions) {
        if (!uniqueSessionsMap.has(session._id)) {
          uniqueSessionsMap.set(session._id, session)
        }
      }
      
      return Array.from(uniqueSessionsMap.values())
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: Infinity,
    enabled: userToken != null,
  }),

  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} params.userToken
   * @param {string} [params.projectId]
   */
  CHAT_SESSION: ({ sessionId, userToken, projectId }) => queryOptions({
    queryKey: ChatSessionQueryKeys.CHAT_SESSION(sessionId),
    queryFn: ({ client, signal }) => (
      getChatSession(sessionId, userToken, { signal }).then((data) => {
        client.setQueryData(
          ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
          ChatSessionQueryActions.UPDATE_SESSION_LIST_ENTRY(sessionId, data.session, { replace: true }),
        )
        return data.session
      })
    ),
    staleTime: FIVE_MINUTES_MS,
    enabled: userToken != null && sessionId != null,
  }),
}

const ChatSessionMutationOptions = {
  /**
   * @param {Object} params
   * @param {string} params.userToken
   * @param {string} [params.projectId]
   */
  CREATE_NEW_SESSION: ({ userToken, projectId }) => mutationOptions({
    mutationFn: async (_, context) => {
      // Empty-session reuse removed — relied on chat?.length which stopped
      // being populated. Will revisit once message pagination lands.
      return await createChatSession(userToken, { projectId })
    },
    async onSuccess(data, variables, _, context) {
      // @ts-ignore
      if (data.isCancelled) return

      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId)
      })
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION(data.session._id),
      })

      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
        ChatSessionQueryActions.ADD_SESSION_LIST_ENTRY(data.session),
      )
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(data.session._id),
        data.session
      )
      // Pre-seed an empty messages page so isLoading is never true for a brand-new session,
      // preventing the skeleton flash before the first message is sent.
      context.client.setQueryData(
        ChatMessageQueryKeys.CHAT_MESSAGES(data.session._id),
        { pages: [{ messages: [], totalCount: 0, hasMore: false }], pageParams: [0] }
      )
    },
  }),
  /**
   * @param {Object} params
   * @param {string} params.userToken
   * @param {string} [params.projectId]
   */
  UPDATE_SESSION: ({ userToken, projectId }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {string} params.sessionId
     * @param {Partial<ChatSession>} params.sessionInfo
     */
    mutationFn: ({ sessionId, sessionInfo }) => (
      updateChatSession(sessionId, sessionInfo, userToken)
    ),
    async onMutate(variables, context) {
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
      })
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId)
      })

      const oldData = context.client.getQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId)
      )
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
        /** @param {ChatSessionQueryData} session */
        session => ({ ...session, ...variables.sessionInfo })
      )
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
        ChatSessionQueryActions.UPDATE_SESSION_LIST_ENTRY(variables.sessionId, variables.sessionInfo),
      )

      return {
        session: oldData,
      }
    },
    async onSuccess(data, variables, mutationResult, context) {
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId)
      })
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
      })

      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
        data.session
      )
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
        ChatSessionQueryActions.UPDATE_SESSION_LIST_ENTRY(variables.sessionId, data.session, { replace: true }),
      )
    },
    async onError(error, variables, mutationResult, context) {
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId)
      })
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
      })

      const prevSessionInfo = Object.fromEntries(
        Object.entries(mutationResult.session)
          .filter(([key, _]) => key in variables.sessionInfo)
      )

      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
        /** @param {ChatSessionQueryData} session */
        session => ({ ...session, ...prevSessionInfo })
      )
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
        ChatSessionQueryActions.UPDATE_SESSION_LIST_ENTRY(variables.sessionId, prevSessionInfo),
      )
    }
  }),
  /**
   * @param {Object} params
   * @param {string} params.userToken
   * @param {string} [params.projectId]
   */
  DELETE_SESSION: ({ userToken, projectId }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {string} params.sessionId
     */
    mutationFn: ({ sessionId }) => (
      deleteChatSession(sessionId, userToken)
    ),
    async onMutate(variables, context) {
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId)
      })
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
      })

      const deletedSession = context.client.getQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId)
      )

      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
        null
      )
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
        ChatSessionQueryActions.REMOVE_SESSION_LIST_ENTRY(variables.sessionId),
      )

      return {
        session: deletedSession,
      }
    },
    async onError(error, variables, mutationResult, context) {
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId)
      })
      await context.client.cancelQueries({
        queryKey: ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
      })
      
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
        mutationResult.session
      )
      context.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId),
        ChatSessionQueryActions.ADD_SESSION_LIST_ENTRY(mutationResult.session)
      )
    }
  }),
}

// isEmptyTextOnlyChatSession removed — see CREATE_NEW_SESSION comment above.

export {
  ChatSessionQueryKeys,
  ChatSessionQueryActions,
  ChatSessionQueryOptions,
  ChatSessionMutationOptions,
}
